import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';

import downIcon from '../../assets/images/down-chevron.svg';
import upIcon from '../../assets/images/up-chevron.svg';
import favoritewhite from '../../assets/images/favoritewhite.svg';
import { getIndividualAvatar } from '../../utils/avatarImages';
import favoriteyellow from '../../assets/images/favoriteyellow.svg';

import config from '../../config';
import { handleResponse } from '../../utils/handleResponse';
import RankingParticipantLabel from '../../components/RankingParticipantLabel';
import {
  LAP_DISTANCE_KM,
  formatDistanceKm,
  resolveDisplayDistanceKm,
} from '../../utils/utils';
import { Row, Col } from 'react-flexbox-grid';

// Helpers para formatear tiempo
const formatTimeHHMMSS = (seconds) => {
  if (!seconds) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const hh = h < 10 ? `0${h}` : h;
  const mm = m < 10 ? `0${m}` : m;
  const ss = s < 10 ? `0${s}` : s;
  return `${hh}:${mm}:${ss}`;
};

const formatPaceMMSS = (paceSeconds) => {
  if (!paceSeconds || paceSeconds <= 0) return '--:--';
  const min = Math.floor(paceSeconds / 60);
  const sec = Math.floor(paceSeconds % 60);
  const mm = min < 10 ? `0${min}` : min;
  const ss = sec < 10 ? `0${sec}` : sec;
  return `${mm}:${ss}`;
};

const TableRankingIndividualItem = ({
  userInfo,
  firstRaking,
  darkMode,
  individualFavorites,
  saveFavorite,
  deleteFavorite,
  setShowLoading,
  favoritesEnabled,
}) => {
  // Controla el acordeón
  const [isOpen, setIsOpen] = useState(false);

  // Almacena las vueltas retornadas por el backend
  const [laps, setLaps] = useState([]);

  // Guardamos info de la vuelta más rápida y la más lenta
  const [fastestPace, setFastestPace] = useState(null);
  const [slowestPace, setSlowestPace] = useState(null);

  // Cuando laps cambie, calculamos la más rápida y la más lenta
  useEffect(() => {
    if (laps.length > 0) {
      const paces = laps.map((lap) => lap.elapsedTime / LAP_DISTANCE_KM);
      setFastestPace(Math.min(...paces));
      setSlowestPace(Math.max(...paces));
    } else {
      setFastestPace(null);
      setSlowestPace(null);
    }
  }, [laps]);

  // Función para cargar las laps del participante
  const fetchLaps = async (participantId) => {
    try {
      setShowLoading(true);
      let athlete = null;
      try {
        athlete = JSON.parse(localStorage.getItem('userData') || 'null');
      } catch (e) {
        athlete = null;
      }
      const headers = athlete?.userToken
        ? { Authorization: `Bearer ${athlete.userToken}` }
        : undefined;
      const response = await fetch(
        `${config.tncApiUrl}/participant/${participantId}/laps`,
        {
          method: 'GET',
          ...(headers ? { headers } : {}),
        }
      );
      setShowLoading(false);
      const data = await handleResponse(response);
      // El backend envía: data: { message: '...', laps: [ ... ] }
      return data?.data?.laps || [];
    } catch (error) {
      console.error('Error fetching laps:', error);
      setShowLoading(false);
      return [];
    }
  };

  // Abre/cierra el acordeón
  const toggleAccordion = async () => {
    if (!isOpen) {
      // Se abre => fetch laps
      const loadedLaps = await fetchLaps(userInfo.id);
      setLaps(loadedLaps);
      setIsOpen(true);
    } else {
      // Se cierra => limpiar laps
      setIsOpen(false);
      setLaps([]);
    }
  };

  const handleFavoriteClick = (event) => {
    event.stopPropagation();
    let saveFunction = saveFavorite;
    let favoriteId = userInfo.user_id;

    if (individualFavorites) {
      individualFavorites.forEach((individualFavorite) => {
        if (individualFavorite.favoriteId === userInfo.user_id) {
          favoriteId = individualFavorite.favoriteId;
          saveFunction = deleteFavorite;
        }
      });
    }
    saveFunction(favoriteId);
  };

  // Determina el ícono de favorito y la función
  const addButtonText = () => {
    if (!favoritesEnabled) return null;
    let favoriteIcon = favoritewhite;

    if (individualFavorites) {
      individualFavorites.forEach((individualFavorite) => {
        if (individualFavorite.favoriteId === userInfo.user_id) {
          favoriteIcon = favoriteyellow;
        }
      });
    }
    return (
      <div
        onClick={handleFavoriteClick}
        className='add-button'
        role='presentation'
      >
        <img src={favoriteIcon} alt='favorite icon' />
      </div>
    );
  };

  const handleRowKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleAccordion();
    }
  };

  const displayDistanceKm = resolveDisplayDistanceKm(
    userInfo.total_distance,
    laps.length
  );

  const calculateAvgPace = (elapsed_time, distanceKm) => {
    if (!elapsed_time || distanceKm <= 0) {
      return (
        <div>
          <Tooltip
            enterTouchDelay={100}
            title='No hay datos disponibles'
            placement='top'
          >
            <span>⚠️</span>
          </Tooltip>
        </div>
      );
    }
    const paceSeconds = elapsed_time / distanceKm;
    return formatPaceMMSS(paceSeconds);
  };

  // Muestra el tiempo total en formato hh:mm:ss + la diferencia con el líder
  const showTime = (elapsed_time) => {
    if (elapsed_time) {
      const mainTime = formatTimeHHMMSS(elapsed_time);
      let diffTime = null;
      if (
        firstRaking &&
        firstRaking.id !== userInfo.id &&
        firstRaking.elapsed_time
      ) {
        const diff = Math.abs(elapsed_time - firstRaking.elapsed_time);
        diffTime = formatTimeHHMMSS(diff);
      }
      return (
        <div className='ranking-time-values'>
          <span className='ranking-time-values__main'>{mainTime}</span>
          {diffTime && (
            <div className='table-ranking-item_user-info-team'>
              <span>+ {diffTime}</span>
            </div>
          )}
        </div>
      );
    }
    return (
      <div>
        <Tooltip
          enterTouchDelay={100}
          title='No hay datos disponibles'
          placement='top'
        >
          <span>⚠️</span>
        </Tooltip>
      </div>
    );
  };

  return (
    <Col
      xs={12}
      className={`table-ranking-item${darkMode ? ' darkMode' : ''}${
        isOpen ? ' table-ranking-item--expanded' : ''
      }`}
    >
      <Row className='table-ranking-item_column-container'>
        <Col xs={12}>
          <Row
            className={`table-ranking-item_column-container_main individual is-clickable${
              favoritesEnabled ? '' : ' no-favorites'
            }${isOpen ? ' is-open' : ''}`}
            onClick={toggleAccordion}
            onKeyDown={handleRowKeyDown}
            role='button'
            tabIndex={0}
            aria-expanded={isOpen}
          >
            {/* Columna NOMBRE / AVATAR / FAVORITO */}
            <Col xs={5} md={6} className='table-ranking-item_column name-column'>
              <div className='table-ranking-item_leading'>
                {addButtonText()}
                <div className='position-text'>{userInfo.ranking}</div>
                <div className='table-ranking-item_user-img-container avatar-12h-container'>
                  <img
                    src={getIndividualAvatar(userInfo.gender)}
                    alt=''
                    className='table-ranking-item_user-img avatar-12h'
                  />
                </div>
              </div>
              <div className='table-ranking-item_user-info-container'>
                <div className='table-ranking-item_user-info-name'>
                  {userInfo.name} {userInfo.last_name}
                </div>
              </div>
            </Col>

            {/* Columna KM */}
            <Col xs={2} md={2} className='table-ranking-item_column stat-column'>
              <div className='table-ranking-item_user-info-name'>
                {userInfo.race_status === 3 || userInfo.race_status === 4
                  ? '--'
                  : formatDistanceKm(displayDistanceKm)}
              </div>
            </Col>

            {/* Columna Avg Pace */}
            <Col xs={2} className='table-ranking-item_column stat-column'>
              <div className='table-ranking-item_user-info-name'>
                {userInfo.race_status === 3 || userInfo.race_status === 4
                  ? '--'
                  : calculateAvgPace(
                      userInfo.elapsed_time,
                      displayDistanceKm
                    )}
              </div>
            </Col>

            {/* Columna TIEMPO */}
            <Col
              xs={3}
              md={2}
              className='table-ranking-item_column stat-column table-ranking-item_column--time'
            >
              <div className='table-ranking-item_time-cell'>
                <div className='ranking-time-stack'>
                  <div className='ranking-time-stack__values'>
                    {userInfo.race_status === 3
                      ? 'DNF'
                      : userInfo.race_status === 4
                      ? 'DQ'
                      : showTime(userInfo.elapsed_time)}
                  </div>
                  <img
                    src={isOpen ? upIcon : downIcon}
                    alt=''
                    className='down-icon ranking-time-chevron hide-mobile'
                    aria-hidden
                  />
                  <img
                    src={isOpen ? upIcon : downIcon}
                    alt=''
                    className='down-icon ranking-time-chevron hide-desktop'
                    aria-hidden
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Col>

        {isOpen && (
          <Col xs={12} className='individual-accordion'>
            <section
              className='team-roster team-roster--participant'
              aria-label='Participante'
            >
              <p className='team-roster__label'>
                <span className='team-roster__label-title'>Participante</span>
              </p>
              <ul className='team-roster__grid team-roster__grid--single'>
                <li className='team-roster__chip team-roster__chip--fit'>
                  <div className='team-roster__avatar'>
                    <img
                      src={getIndividualAvatar(userInfo.gender)}
                      alt=''
                    />
                  </div>
                  <RankingParticipantLabel
                    person={userInfo}
                    className='team-roster__fullname team-roster__fullname--single-line'
                    nameClassName='team-roster__name'
                    bibClassName='team-roster__bib'
                  />
                </li>
              </ul>
            </section>

            {laps.length > 0 && (
              <div className='individual-accordion-laps'>
            {laps
              .slice()
              .sort((a, b) => a.lapNumber - b.lapNumber)
              .map((lap, index) => {
                const paceSec = lap.elapsedTime / LAP_DISTANCE_KM;
                const isFastest = paceSec === fastestPace;
                const isSlowest = paceSec === slowestPace;
                const paceClass =
                  isFastest && isSlowest
                    ? ''
                    : isFastest
                    ? ' ranking-lap-value--fast'
                    : isSlowest
                    ? ' ranking-lap-value--slow'
                    : '';

                return (
                  <Row
                    key={lap.id || index}
                    className='table-ranking-item_column-container_secundary individual'
                  >
                    <Col
                      xs={5}
                      className='table-ranking-item_column ranking-lap-name'
                    >
                      <div className='table-ranking-item_user-info-name ranking-lap-prefix'>
                        LAP/
                      </div>
                      <div className='position-text ranking-lap-index'>
                        {lap.lapNumber}
                      </div>
                    </Col>

                    <Col xs={2} md={2} className='table-ranking-item_column stat-column'>
                      <div
                        className={`table-ranking-item_user-info-name ranking-lap-value${paceClass}`}
                      >
                        {formatDistanceKm(LAP_DISTANCE_KM)}
                      </div>
                    </Col>
                    <Col xs={2} className='table-ranking-item_column stat-column'>
                      <div
                        className={`table-ranking-item_user-info-name ranking-lap-value${paceClass}`}
                      >
                        {formatPaceMMSS(paceSec)}
                      </div>
                    </Col>
                    <Col xs={3} md={2} className='table-ranking-item_column stat-column'>
                      <div
                        className={`table-ranking-item_user-info-name ranking-lap-value${paceClass}`}
                      >
                        {formatTimeHHMMSS(lap.elapsedTime)}
                      </div>
                    </Col>
                  </Row>
                );
              })}
              </div>
            )}

            {laps.length === 0 && (
              <div className='individual-accordion-laps'>
                <Row className='table-ranking-item_column-container_secundary individual team-accordion-empty'>
                  <Col
                    xs={12}
                    className='table-ranking-item_column ranking-lap-name'
                  >
                    <div className='table-ranking-item_user-info-name'>
                      No hay vueltas registradas
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Col>
        )}
      </Row>
    </Col>
  );
};

TableRankingIndividualItem.propTypes = {
  userInfo: PropTypes.object.isRequired,
  firstRaking: PropTypes.object.isRequired,
  individualFavorites: PropTypes.array.isRequired,
  saveFavorite: PropTypes.func.isRequired,
  deleteFavorite: PropTypes.func.isRequired,
  setShowLoading: PropTypes.func.isRequired,
  darkMode: PropTypes.bool,
  favoritesEnabled: PropTypes.bool,
};

TableRankingIndividualItem.defaultProps = {
  favoritesEnabled: false,
  darkMode: false,
};

export default TableRankingIndividualItem;
